import { AppUtils } from '../../shared/utils/index';
import { User } from '../models/api/User/User';
import { BasicMglConnectionService } from './BasicMglConnectionService';
import { MglCustomerModel } from '../models/transport/MglCustomer';

export class BasicUserService<T extends BasicAppDefinition> {
	protected readonly identityUserProvider: IndentityUserProvider;
	protected readonly mglCustomerUserProvider: MglCustomerUserProvider<T>;

	/**
	 * @param options - the application state
	 * @param scope - optional OAuth scope to control which user data to get
	 */
	constructor(options: BasicAppOptions<T>, scope?: string) {
		this.mglCustomerUserProvider = new MglCustomerUserProvider(options);
		this.identityUserProvider = new IndentityUserProvider(scope || T.Utils.Auth.scope);
	}


	/**
	 * Tries to get the user model, if the user is currently logged in or just finished the 1-step membership enclosure.
	 * (Currently this call may result in several redirects to get a token for reading the user data)
	 * Callback receives user model with user data or empty user model (check with Valid) if the user is not logged in.
	 */
	public getUser(callback: (user: User) => void): void {

		this.tryGetUserFromProviders((user) => {
			if (user) {
				callback(user);
			} else {
				callback(new User());
			}
		});
	}

	/**
	 * Invalidate User to reset its state for new user retrieval from webservice
	 */
	public invalidate(): void {
		this.clearUserPersistence();
	}

	/**
	 * Clear the persisted user used for user initialization
	 */
	protected clearUserPersistence(): void {
		this.identityUserProvider.clearUserPersistence();
		this.mglCustomerUserProvider.clearUserPersistence();
	}

	/**
	 * Tries to get a user from the 1-step membership enclosure. If not available then tries to get a user from the login.
	 *
	 * @param callback - name callback: the callback to call with optional user after processing finished.
	 */
	protected tryGetUserFromProviders(callback: (user?: User) => void): void {
		const mglUserData = this.mglCustomerUserProvider.getUserData();
		if (mglUserData) {
			this.mglCustomerUserProvider.clearUserPersistence();
			callback(User.createFromObject(mglUserData));
		} else {
			this.identityUserProvider.getUserData((identityUserData) => {
				if (identityUserData) {
					callback(User.createFromObject(identityUserData));
				} else {
					callback();
				}
			});
		}
	}
}

/**
 * Gets the user data from the login, if the user is logged in.
 */
class IndentityUserProvider {
	protected readonly scope: string;
	constructor(scope: string) {
		this.scope = scope;
	}

	/**
	 * Gets the user data from the application identity. Currently this call may result in several redirects to get a token for reading the user data.
	 *
	 * @param callback - name callback: the callback to call with optional user data after processing finished.
	 */
	public getUserData(callback: (dataObject: { [key: string]: any }) => void): void {
		T.Utils.Auth.getResolvedIdentity(callback, {
			scope: this.scope
		});
	}
	/**
	 * Clear the persisted user used for user initialization
	 */
	public clearUserPersistence(): void {
		T.Utils.Store.set('Application.Identity', null, T.Utils.Store.SESSION);
	}
}

/**
 * Gets the user data from the 1-step membership enclosure, if available.
 */
class MglCustomerUserProvider<T extends BasicAppDefinition> {
	protected readonly mglConnectionService: BasicMglConnectionService<T>;
	constructor(options: BasicAppOptions<T>) {
		this.mglConnectionService = new BasicMglConnectionService(options);
	}

	/**
	 * Gets the user form data from the 1-step membership enclosure and maps it to user data format.
	 */
	public getUserData(): { [key: string]: any } | undefined {
		const mglCustomer = this.mglConnectionService.retrieveCustomer();
		if (mglCustomer.Valid) {
			return this.mapMglCustomer(mglCustomer);
		}
		return undefined;
	}
	/**
	 * No need to clear anymore, because of changed implementation (124008) that ensures customer data stays within one application.
	 */
	public clearUserPersistence(): void {
		// nothing to do
	}

	/**
	 * Maps the mgl customer from membership application form to the user model format.
	 *
	 */
	protected mapMglCustomer(mglCustomer: MglCustomerModel): { [key: string]: any } {
		return {
			MglNo: mglCustomer.apiData.Mitgliedsnummer,
			Roles: ["Neues Mitglied"],
			UserData: {
				Anrede: mglCustomer.Antragssteller.Anrede,
				Titel: mglCustomer.Antragssteller.Titel,
				Anschrift: mglCustomer.apiData.Anschrift,
				Vorname: mglCustomer.apiData.Vorname,
				Nachname: mglCustomer.apiData.Nachname,
				Geburtsdatum: AppUtils.Format.viewDateToIsoDate(mglCustomer.dob),
				Email: mglCustomer.Antragssteller.Email,
				Telefon: mglCustomer.Antragssteller.Telefon,
				TelGesch: mglCustomer.Antragssteller.TelGesch,
				HatAbweichendenKontoInhaber: mglCustomer.HatAbweichendenKontoInhaber,
				Bankdaten: {
					Iban: mglCustomer.Bankdaten.Iban,
					AbwKontoInhaber: mglCustomer.Bankdaten.AbwKontoInhaber,
				},
				Mitgliedschaft: {
					PlusMitglied: mglCustomer.membership === 2
				}
			},
			Token: mglCustomer.apiData.Token
		};
	}
}
